import { useState, useEffect } from "react";
import { BackgroundLayer } from "@/components/BackgroundLayer";
import { getStoredBackground } from "@/lib/backgrounds";
import RequestAccessForm from "@/components/RequestAccessForm";
import MarketPulse from "@/components/MarketPulse";
import { useAuth } from "@/lib/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { BarChart3, Lock, Key, Eye, EyeOff, AlertCircle, Shield, Globe, Fingerprint, X, Trophy , ChevronDown } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function getMarketSchedule(isOpen) {
  const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  const day = nowET.getDay();
  if (isOpen) {
    const close = new Date(nowET);
    close.setHours(16, 0, 0, 0);
    return { label: "Closes in", target: close };
  }
  const h = nowET.getHours();
  const next = new Date(nowET);
  const isWeekday = day >= 1 && day <= 5;
  const beforeOpenToday = isWeekday && (h < 9 || (h === 9 && nowET.getMinutes() < 30));
  if (!beforeOpenToday) {
    next.setDate(next.getDate() + 1);
    while (next.getDay() === 0 || next.getDay() === 6) next.setDate(next.getDate() + 1);
  }
  next.setHours(9, 30, 0, 0);
  return { label: "Opens in", target: next };
}

function formatCountdown(targetET) {
  const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  let ms = targetET.getTime() - nowET.getTime();
  if (ms < 0) ms = 0;
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Biometric localStorage helpers
function saveBiometricData(email, info) {
  try {
    const data = JSON.parse(localStorage.getItem("rj_biometric") || "{}");
    data[email.toLowerCase()] = info;
    localStorage.setItem("rj_biometric", JSON.stringify(data));
    localStorage.setItem("rj_biometric_email", email.toLowerCase());
  } catch {}
}

function getBiometricData(email) {
  try {
    const data = JSON.parse(localStorage.getItem("rj_biometric") || "{}");
    return data[email?.toLowerCase()] || null;
  } catch { return null; }
}

export default function LoginPage() {
  const { user, login, biometricLogin } = useAuth();
  const [mode, setMode] = useState(() => new URLSearchParams(window.location.search).get("request") ? "register" : "login");
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("rj_remembered_email") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(() => {
    try { return !!localStorage.getItem("rj_remembered_email"); } catch { return false; }
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clientIp, setClientIp] = useState("");
  const [settings, setSettings] = useState({ copyright: "", version: "" });
  const [leaderboard, setLeaderboard] = useState({ users: [], benchmark: null });
  const [leaderboardUpdated, setLeaderboardUpdated] = useState(null);
  const [lbOpen, setLbOpen] = useState(() => {
    try { return localStorage.getItem("rj_login_lb_open") !== "0"; } catch { return true; }
  });
  const toggleLb = () => setLbOpen(o => {
    try { localStorage.setItem("rj_login_lb_open", o ? "0" : "1"); } catch {}
    return !o;
  });

  // Auto-refresh leaderboard every 60 seconds
  useEffect(() => {
    const fetchLeaderboard = () => {
      axios.get(`${BACKEND}/api/woodrow/leaderboard`).then(r => {
        setLeaderboard(r.data || { users: [], benchmark: null });
        setLeaderboardUpdated(new Date());
      }).catch(() => {});
    };
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Biometric state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricNeverAsk, setBiometricNeverAsk] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/client-ip`).then(r => setClientIp(r.data?.ip || "")).catch(() => {});
    axios.get(`${BACKEND}/api/settings/public`).then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  // Check biometric availability
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    const checkBiometric = async () => {
      try {
        if (window.PublicKeyCredential) {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricAvailable(available);
          const lastEmail = localStorage.getItem("rj_biometric_email");
          if (lastEmail) {
            const bd = getBiometricData(lastEmail);
            if (bd) {
              setBiometricEnabled(!!bd.credentialId);
              setBiometricNeverAsk(bd.neverAsk === true);
            }
          }
        }
      } catch {
        setBiometricAvailable(false);
      }
    };
    checkBiometric();
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (user && !showBiometricPrompt) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (rememberEmail) {
        try { localStorage.setItem("rj_remembered_email", email); } catch {}
      } else {
        try { localStorage.removeItem("rj_remembered_email"); } catch {}
      }
      const userData = await login(email, password);
      // After successful password login, check if we should offer biometric setup
      if (isMobile && biometricAvailable && !biometricEnabled && !biometricNeverAsk) {
        setPendingUser({ email: email.toLowerCase(), name: userData?.name });
        setShowBiometricPrompt(true);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  const setupBiometric = async () => {
    try {
      const userEmail = pendingUser?.email || email;
      if (!userEmail) { setShowBiometricPrompt(false); return; }
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: "Rockafeller Jones", id: window.location.hostname },
          user: {
            id: new Uint8Array(16),
            name: userEmail,
            displayName: pendingUser?.name || "User",
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },
            { alg: -257, type: "public-key" },
          ],
          authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
          timeout: 60000,
          attestation: "none",
        },
      });
      if (credential) {
        const rawIdBase64 = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        saveBiometricData(userEmail, {
          enabled: true,
          credentialId: credential.id,
          rawId: rawIdBase64,
          setupDate: new Date().toISOString(),
        });
        setBiometricEnabled(true);
      }
    } catch (err) {
      console.error("Biometric setup error:", err);
    }
    setShowBiometricPrompt(false); // This will allow Navigate to fire
  };

  const handleBiometricLogin = async () => {
    setError("");
    try {
      const lastEmail = localStorage.getItem("rj_biometric_email");
      const bd = lastEmail ? getBiometricData(lastEmail) : null;
      if (!bd?.rawId) {
        setError("No biometric data found. Please login with password first.");
        return;
      }
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);
      const rawIdArray = Uint8Array.from(atob(bd.rawId), c => c.charCodeAt(0));
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: [{ id: rawIdArray, type: "public-key" }],
          timeout: 60000,
          userVerification: "preferred",
        },
      });
      if (assertion) {
        setSubmitting(true);
        await biometricLogin(lastEmail);
        setSubmitting(false);
      }
    } catch (err) {
      setSubmitting(false);
      if (err.name === "NotAllowedError") {
        setError("Biometric cancelled or not recognized. Try again or use password.");
      } else if (err.name === "InvalidStateError") {
        setError("Biometric credential not found on this device. Use password to login.");
      } else {
        setError(`Biometric error: ${err.message || err.name}. Try using password.`);
      }
    }
  };

  const skipBiometric = () => setShowBiometricPrompt(false);

  const neverAskBiometric = () => {
    const userEmail = pendingUser?.email || email;
    if (userEmail) {
      saveBiometricData(userEmail, { enabled: false, neverAsk: true });
      setBiometricNeverAsk(true);
    }
    setShowBiometricPrompt(false);
  };

  const biometricEmail = localStorage.getItem("rj_biometric_email");
  const loginBg = getStoredBackground();

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${loginBg === "none" ? "bg-bt-bg" : ""}`} data-testid="login-page">
      <BackgroundLayer id={loginBg} strength={0.55} />
      {/* Glow background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-bt-gold/5 rounded-full blur-[120px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="relative login-card-frame p-[1px] shadow-[0_20px_80px_rgb(0_0_0/0.35),0_0_60px_rgb(var(--bt-gold)/0.12)]" data-testid="login-card">
          <div className="login-aura" aria-hidden="true" />
          <div className="login-border-ring" aria-hidden="true"><div className="login-border-spin" /></div>
          <div className="relative bg-bt-bg/80 backdrop-blur-xl p-8">
            {["top-2 left-2 border-t-2 border-l-2", "top-2 right-2 border-t-2 border-r-2", "bottom-2 left-2 border-b-2 border-l-2", "bottom-2 right-2 border-b-2 border-r-2"].map(c => (
              <span key={c} className={`login-corner absolute w-5 h-5 border-bt-gold pointer-events-none ${c}`} aria-hidden="true" />
            ))}
            {/* Logo */}
            <div className="flex flex-col items-center mb-6">
              <div className="login-logo w-16 h-16 bg-bt-gold/15 border border-bt-gold/30 flex items-center justify-center mb-4">
                <BarChart3 className="w-9 h-9 text-bt-gold" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-bt-text-primary tracking-tight">Rockafeller Jones</h1>
              <svg viewBox="0 0 200 24" className="w-40 h-5 mt-2" aria-hidden="true" data-testid="login-sparkline">
                <defs>
                  <linearGradient id="login-spark" x1="0" x2="1">
                    <stop offset="0%" stopColor="rgb(var(--bt-gold))" stopOpacity="0" />
                    <stop offset="50%" stopColor="rgb(var(--bt-gold))" stopOpacity="1" />
                    <stop offset="100%" stopColor="rgb(var(--bt-positive))" stopOpacity="1" />
                  </linearGradient>
                </defs>
                <path className="login-spark-line" d="M0 18 L18 16 L32 19 L48 12 L62 14 L78 8 L92 11 L108 6 L124 9 L140 4 L156 7 L172 3 L188 5 L200 1"
                  fill="none" stroke="url(#login-spark)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle className="login-spark-dot" cx="200" cy="1" r="2" fill="rgb(var(--bt-positive))" />
              </svg>
              <div className="flex items-center gap-2 mt-1 text-bt-text-secondary">
                <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-sm font-body">Secure Access Required</span>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-bt-negative text-sm mb-4 bg-bt-negative/10 border border-bt-negative/20 px-3 py-2" data-testid="auth-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Biometric Login Button */}
            {biometricEnabled && isMobile && mode === "login" && (
              <div className="mb-4">
                <Button
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={submitting}
                  className="w-full h-12 bg-bt-gold/10 border border-bt-gold/40 text-bt-gold hover:bg-bt-gold/20 font-body font-medium text-base transition-all"
                  data-testid="biometric-login-btn"
                >
                  <Fingerprint className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  {submitting ? "Verifying..." : "Sign In with Fingerprint"}
                </Button>
                <p className="text-center text-[10px] text-bt-text-tertiary mt-1.5 font-mono">
                  {biometricEmail}
                </p>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-bt-border-subtle" /></div>
                  <div className="relative flex justify-center"><span className="bg-bt-bg px-3 text-xs font-mono text-bt-text-tertiary">or use password</span></div>
                </div>
              </div>
            )}

            {mode === "register" ? <RequestAccessForm /> : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">Email Address</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  placeholder="Enter your email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="mt-1.5 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono placeholder:text-bt-text-tertiary h-11"
                  data-testid="auth-email-input"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Key className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
                  <Label className="text-bt-text-primary font-body text-sm font-medium">PIN / Password</Label>
                </div>
                <div className="relative">
                  <Input
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="pl-11 pr-11 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono placeholder:text-bt-text-tertiary h-11 text-center tracking-[0.3em]"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-bt-text-tertiary hover:text-bt-text-primary transition-colors"
                    title={showPw ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                  >
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberEmail}
                  onCheckedChange={setRememberEmail}
                  className="border-bt-border-focus data-[state=checked]:bg-bt-gold data-[state=checked]:border-bt-gold"
                  data-testid="remember-email-checkbox"
                />
                <label htmlFor="remember" className="text-sm text-bt-text-secondary font-body cursor-pointer">
                  Remember my email
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="login-sheen relative overflow-hidden w-full h-12 bg-bt-surface-2 border border-bt-gold/30 text-bt-gold hover:bg-bt-gold/15 hover:border-bt-gold/50 font-body font-medium text-base shadow-[0_0_20px_rgb(var(--bt-gold)/0.1)] transition-all"
                data-testid="auth-submit-btn"
              >
                {submitting ? "..." : <><Lock className="w-4 h-4 mr-2" /> Unlock Portfolio</>}
              </Button>
            </form>
            )}

            <MarketPulse />

            {/* Session info */}
            <div className="mt-4 flex items-center justify-between text-xs text-bt-text-tertiary">
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-40" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-bt-positive" />
                </span>
                <span className="font-mono">Session Protected</span>
              </div>
              {clientIp && (
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3 h-3" strokeWidth={1.5} />
                  <span className="font-mono">{clientIp}</span>
                </div>
              )}
            </div>
            <p className="text-center text-[10px] text-bt-text-tertiary mt-2 font-body">
              Your data is encrypted and secure
            </p>

            <div className="mt-6 pt-4 border-t border-bt-border-subtle text-center">
              <p className="text-[10px] text-bt-text-tertiary font-mono">
                &copy; {settings.copyright || "2025/2026 - Rockafeller Jones. All rights reserved."} {settings.version || "v1.0"}
              </p>
              <Link to="/legal" className="text-[10px] font-mono text-bt-text-tertiary underline decoration-dotted hover:text-bt-gold" data-testid="login-legal-link">
                Disclaimer &amp; Copyright · Not financial advice
              </Link>
            </div>

            <div className="mt-4 flex items-center justify-center gap-3">
              <Link to="/learn" className="text-sm text-bt-gold/70 hover:text-bt-gold transition-colors font-body underline underline-offset-4" data-testid="login-learn-more-link">
                Learn More
              </Link>
              <span className="text-bt-text-tertiary text-xs">·</span>
              <button
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="text-sm text-bt-gold/70 hover:text-bt-gold transition-colors font-body underline underline-offset-4"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "Request Access Here" : "Already have access? Sign in"}
              </button>
            </div>
          </div>
        </div>

        {/* Woodrow Leaderboard */}
        {leaderboard.users?.length > 0 && (
          <div className="mt-6 relative" data-testid="login-leaderboard">
            <div className="absolute -inset-[1px] bg-gradient-to-b from-bt-gold/20 via-bt-gold/5 to-bt-gold/20 blur-[1px]" />
            <div className="relative bg-bt-bg/80 backdrop-blur-xl border border-bt-gold/15 p-5">
              <div className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 ${lbOpen ? "mb-3" : ""}`}>
                <button
                  type="button"
                  onClick={toggleLb}
                  className="flex items-center gap-2 group shrink-0"
                  aria-expanded={lbOpen}
                  data-testid="login-leaderboard-toggle"
                >
                  <Trophy className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
                  <span className="text-xs font-mono uppercase tracking-wider text-bt-gold">Woodrow Leaderboard</span>
                  <ChevronDown className={`w-4 h-4 text-bt-gold/70 group-hover:text-bt-gold transition-transform duration-300 ${lbOpen ? "" : "-rotate-90"}`} />
                </button>
                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 min-w-0">
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <span className="relative flex h-1.5 w-1.5">
                      {leaderboard.stock_market_open && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-40" />}
                      <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${leaderboard.stock_market_open ? 'bg-bt-positive' : 'bg-bt-text-tertiary'}`} />
                    </span>
                    <span className={`text-[9px] font-mono ${leaderboard.stock_market_open ? 'text-bt-positive' : 'text-bt-text-tertiary'}`}>
                      STOCKS {leaderboard.stock_market_open ? 'OPEN' : 'CLOSED'}
                    </span>
                    {(() => {
                      const sched = getMarketSchedule(leaderboard.stock_market_open);
                      return <span className="hidden sm:inline text-[9px] font-mono text-bt-text-tertiary ml-1">· {sched.label} {formatCountdown(sched.target)}</span>;
                    })()}
                  </div>
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-40" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-bt-positive" />
                    </span>
                    <span className="text-[9px] font-mono text-bt-positive">CRYPTO 24/7</span>
                  </div>
                  {leaderboardUpdated && (
                    <span className="hidden sm:inline text-[9px] font-mono text-bt-text-tertiary">LIVE</span>
                  )}
                </div>
              </div>
              {!lbOpen && (() => {
                const top = leaderboard.users.find(u => !u.is_sp_tracker);
                return top ? (
                  <button type="button" onClick={toggleLb} className="mt-2 w-full flex items-center justify-between px-2 text-xs font-mono" data-testid="login-leaderboard-summary">
                    <span className="text-bt-text-secondary">Leader: <span className="text-bt-text-primary font-body">{top.name}</span></span>
                    <span className={top.all_time_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}>{top.all_time_pct >= 0 ? "+" : ""}{top.all_time_pct}%</span>
                  </button>
                ) : null;
              })()}
              <div className={`grid transition-[grid-template-rows,opacity] duration-300 ${lbOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`} data-testid="login-leaderboard-body">
              <div className="overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-2 pb-1.5 mb-1 border-b border-bt-border-subtle">
                <span className="text-[10px] font-mono uppercase text-bt-text-tertiary tracking-wider">Trader</span>
                <div className="flex gap-6">
                  <span className="text-[10px] font-mono uppercase text-bt-text-tertiary tracking-wider w-16 text-right">Today</span>
                  <span className="text-[10px] font-mono uppercase text-bt-text-tertiary tracking-wider w-16 text-right">
                    {leaderboard.reset_date
                      ? (() => { const [y, m, d] = leaderboard.reset_date.split("-"); return `Since ${new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`; })()
                      : "All Time"}
                  </span>
                </div>
              </div>
              <div className="space-y-0.5">
                {leaderboard.users.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 hover:bg-bt-surface-2/50 transition-colors" data-testid={`leaderboard-entry-${i}`}>
                    <div className="flex items-center gap-2.5">
                      <span className={`text-xs font-mono w-5 text-center ${i === 0 && !entry.is_sp_tracker ? 'text-bt-gold font-bold' : 'text-bt-text-tertiary'}`}>
                        {entry.is_sp_tracker ? 'S&P' : i + 1}
                      </span>
                      <span className={`text-sm font-body ${entry.is_sp_tracker ? 'italic' : 'text-bt-text-primary'}`} style={entry.is_sp_tracker ? {color: '#6B8DE3'} : {}}>
                        {entry.name}
                      </span>
                    </div>
                    <div className="flex gap-6">
                      <span className={`text-sm font-mono w-16 text-right ${entry.today_pct >= 0 ? 'text-bt-positive' : 'text-bt-negative'}`}>
                        {entry.today_pct >= 0 ? '+' : ''}{entry.today_pct}%
                      </span>
                      <span className={`text-sm font-mono font-medium w-16 text-right ${entry.all_time_pct >= 0 ? 'text-bt-positive' : 'text-bt-negative'}`}>
                        {entry.all_time_pct >= 0 ? '+' : ''}{entry.all_time_pct}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Biometric Setup Prompt */}
      {showBiometricPrompt && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="biometric-prompt">
          <div className="bg-bt-surface border border-bt-gold/30 p-6 max-w-sm w-full shadow-[0_0_60px_rgb(var(--bt-gold)/0.1)]">
            <button onClick={skipBiometric} className="absolute top-3 right-3 text-bt-text-tertiary hover:text-bt-text-primary">
              <X className="w-4 h-4" />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-bt-gold/15 border border-bt-gold/30 flex items-center justify-center mx-auto mb-4">
                <Fingerprint className="w-9 h-9 text-bt-gold" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-xl font-semibold text-bt-text-primary mb-2">
                Enable Fingerprint Login?
              </h2>
              <p className="text-sm text-bt-text-secondary font-body mb-6">
                Use your fingerprint or Face ID to quickly log in next time without entering your password.
              </p>
              <div className="space-y-2">
                <Button
                  onClick={setupBiometric}
                  className="w-full h-11 bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body font-medium"
                  data-testid="biometric-enable-btn"
                >
                  <Fingerprint className="w-4 h-4 mr-2" /> Enable Fingerprint
                </Button>
                <Button
                  onClick={skipBiometric}
                  variant="outline"
                  className="w-full h-11 border-bt-border-focus text-bt-text-secondary hover:text-bt-text-primary font-body"
                  data-testid="biometric-skip-btn"
                >
                  Maybe Later
                </Button>
                <button
                  onClick={neverAskBiometric}
                  className="text-xs text-bt-text-tertiary hover:text-bt-text-secondary transition-colors font-body mt-1"
                  data-testid="biometric-never-btn"
                >
                  Don't ask me again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
